import { useState, useEffect } from 'react'
import TopSafeSpacer from '../layout/TopSafeSpacer'
import { useNavigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { CURRENCIES, PRODUCT_CONDITIONS, PAYMENT_METHODS } from '../../utils/constants'
import { productApi, CategoryNode } from '../../services/api'
import LocationSelector from '../ui/LocationSelector'
import FlexibleTagInput from '../ui/FlexibleTagInput'
import CategoryAttributePicker from '../ui/CategoryAttributePicker'
import MapPointPicker from '../ui/MapPointPicker'
import { MapPin, ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react'
import { t } from '../../i18n/translations'

export default function ProductForm() {
  const navigate = useNavigate()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    quantity: '1',
    condition: 'new',
    category_id: '',
  })

  const [customTags, setCustomTags] = useState<string[]>([])
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([])
  const [categories, setCategories] = useState<CategoryNode[]>([])
  const [selectedPath, setSelectedPath] = useState<CategoryNode[]>([])
  const [productAttributes, setProductAttributes] = useState<Record<string, any>>({})
  const [showSuggestModal, setShowSuggestModal] = useState(false)
  const [suggestName, setSuggestName] = useState('')
  const [suggestNote, setSuggestNote] = useState('')
  const [suggestBusy, setSuggestBusy] = useState(false)
  const [suggestDone, setSuggestDone] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [productLocation, setProductLocation] = useState<{
    country_code: string | null
    city_name: string | null
  }>({ country_code: null, city_name: null })

  const [userHomeLocation, setUserHomeLocation] = useState<{
    country_code: string | null
    city_name: string | null
  } | null>(null)

  // Precise map point (optional). Collapsed by default so Leaflet only mounts on expand.
  const [showMapPicker, setShowMapPicker] = useState(false)
  const [mapPoint, setMapPoint] = useState<{
    lat: number
    lng: number
    radius: number
  } | null>(null)

  // Removed Telegram BackButton - using custom top bar instead

  // Fetch user's home city and categories on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      // Fetch user location
      try {
        const location = await productApi.getUserLocation()

        // Store user's home location for "Same as home region" button
        if (location.home_country_code || location.home_city_name) {
          setUserHomeLocation({
            country_code: location.home_country_code,
            city_name: location.home_city_name
          })

          // Auto-populate product location with user's home city
          logger.info('ProductForm: Auto-populating location from user home city', {
            country: location.home_country_code,
            city: location.home_city_name
          })
          setProductLocation({
            country_code: location.home_country_code,
            city_name: location.home_city_name
          })
        }
      } catch (error) {
        logger.warn('ProductForm: Failed to fetch user location:', error)
      }

      // Fetch category tree
      try {
        const data = await productApi.getCategoryTree('en')
        if (data.categories) {
          setCategories(data.categories)
        }
      } catch (error) {
        logger.warn('ProductForm: Failed to fetch categories:', error)
      }
    }

    fetchInitialData()
  }, [])

  // Handler for "Same as home region" button
  const handleSameAsHomeRegion = () => {
    if (userHomeLocation) {
      setProductLocation({
        country_code: userHomeLocation.country_code,
        city_name: userHomeLocation.city_name
      })
      logger.info('ProductForm: Set location to home region')
    }
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required'
    }

    const price = parseFloat(formData.price)
    if (!formData.price || price <= 0) {
      newErrors.price = 'Price must be greater than 0'
    }

    const quantity = parseInt(formData.quantity)
    if (!formData.quantity || quantity < 0) {
      newErrors.quantity = 'Quantity must be 0 or greater'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    logger.debug('ProductForm: Submit clicked')

    if (!validate()) {
      logger.warn('ProductForm: Validation failed', errors)
      return
    }

    setIsSubmitting(true)

    try {
      // Combine predefined tags and custom tags
      const allTags = [...customTags]

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        price: parseFloat(formData.price),
        currency: formData.currency,
        quantity: parseInt(formData.quantity),
        status: 'active', // Set as active by default
        condition: formData.condition,
        category_id: formData.category_id || undefined,
        attributes: Object.keys(productAttributes).length > 0 ? productAttributes : undefined,
        tags: allTags.length > 0 ? allTags : undefined,
        payment_methods: selectedPaymentMethods.length > 0 ? selectedPaymentMethods : undefined,
        country_code: productLocation.country_code,
        city_name: productLocation.city_name,
        // Only send precise coordinates when the seller actually dropped a pin.
        ...(mapPoint
          ? {
              location_lat: mapPoint.lat,
              location_lng: mapPoint.lng,
              location_radius_km: mapPoint.radius,
            }
          : {}),
      }

      logger.debug('ProductForm: Creating product with payload', payload)

      const result = await productApi.createProduct(payload)
      logger.info('ProductForm: Product created successfully', result)

      // Success - navigate to edit page to add images
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success')
      }

      logger.logNavigation('/product/new', `/product/${result.id}/edit`)
      navigate(`/product/${result.id}/edit`)
    } catch (error) {
      logger.error('ProductForm: Failed to create product:', error)
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to create product' })

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Top padding for Telegram UI */}
      <TopSafeSpacer />

      {/* Custom Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <h1 className="text-lg font-semibold text-gray-800">New Product</h1>
          <div className="w-16" /> {/* Spacer for centering */}
        </div>
      </div>

      <main className="flex-1 overflow-auto p-4 pb-24">
        <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
          {/* Form Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-4">
            <h1 className="text-xl font-bold text-gray-800">Create New Product</h1>
            <p className="text-sm text-gray-600 mt-1">Fill in the details to list your product</p>
          </div>

          {/* Product Name */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
              } text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
              placeholder="Enter product name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Describe your product (optional)"
              rows={4}
            />
          </div>

          {/* Price and Currency */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.price ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                  } text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                  placeholder="0.00"
                />
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} {currency.symbol}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Quantity */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('product.quantity')} *
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.quantity ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
              } text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
              placeholder="1"
              min="0"
            />
            {errors.quantity && (
              <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
            )}
          </div>

          {/* Condition */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('product.condition')}
            </label>
            <select
              value={formData.condition}
              onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            >
              {PRODUCT_CONDITIONS.map((condition) => (
                <option key={condition.value} value={condition.value}>
                  {t(`product.conditions.${condition.value}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          {categories.length > 0 && (
            <>
              <CategoryAttributePicker
                categories={categories}
                selectedPath={selectedPath}
                onSelectCategory={(path) => {
                  setSelectedPath(path)
                  setFormData({ ...formData, category_id: path[path.length - 1]?.id || '' })
                }}
                attributes={productAttributes}
                onAttributesChange={setProductAttributes}
                label={t('product.category')}
              />

              {/* Suggest a missing category */}
              <button
                type="button"
                onClick={() => { setShowSuggestModal(true); setSuggestDone(false) }}
                className="mt-3 text-xs text-blue-600 font-medium hover:underline"
              >
                {t('category.suggestCta')}
              </button>
            </>
          )}

          {/* Payment Methods */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('product.paymentMethods')}
            </label>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_METHODS.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => {
                    setSelectedPaymentMethods(prev =>
                      prev.includes(method.value)
                        ? prev.filter(m => m !== method.value)
                        : [...prev, method.value]
                    )
                  }}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedPaymentMethods.includes(method.value)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t(`product.payments.${method.value}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Predefined Tags */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Tags
            </label>
            <FlexibleTagInput
              tags={customTags}
              onChange={setCustomTags}
              placeholder={t('product.tagsHint')}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('product.tagsHint')}
            </p>
          </div>

          {/* Product Location */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Location
            </label>
            <LocationSelector
              countryCode={productLocation.country_code}
              cityName={productLocation.city_name}
              onChange={(country, city) => {
                setProductLocation({
                  country_code: country,
                  city_name: city
                })
              }}
            />
            {userHomeLocation && userHomeLocation.city_name && (
              <button
                type="button"
                onClick={handleSameAsHomeRegion}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
              >
                <MapPin className="w-4 h-4" />
                <span>Same as home region ({userHomeLocation.city_name})</span>
              </button>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Where is this product located?
            </p>
          </div>

          {/* Precise Location (optional, collapsible) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <button
              type="button"
              onClick={() => setShowMapPicker((prev) => !prev)}
              className="w-full flex items-center justify-between text-left"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <span>📍</span>
                <span>{t('location.preciseLocation')}</span>
              </span>
              {showMapPicker ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            <p className="mt-1 text-xs text-gray-500">
              {t('location.preciseHint')}
            </p>
            {showMapPicker && (
              <div className="mt-3">
                <MapPointPicker
                  lat={mapPoint?.lat ?? null}
                  lng={mapPoint?.lng ?? null}
                  radiusKm={mapPoint?.radius ?? null}
                  onChange={(lat, lng, radius) =>
                    setMapPoint({ lat, lng, radius })
                  }
                />
                {mapPoint && (
                  <button
                    type="button"
                    onClick={() => setMapPoint(null)}
                    className="mt-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                  >
                    {t('location.clearPoint')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              isSubmitting
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isSubmitting ? 'Creating Product...' : 'Create Product'}
          </button>
        </form>

        {/* Suggest-a-category modal */}
        {showSuggestModal && (
          <div
            className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowSuggestModal(false) }}
          >
            <div className="bg-white rounded-2xl p-5 max-w-md w-full shadow-xl">
              <h3 className="text-lg font-bold text-gray-800 mb-1">{t('category.suggestTitle')}</h3>
              <p className="text-sm text-gray-500 mb-4">{t('category.suggestHint')}</p>
              {suggestDone ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 mb-4">
                  {t('category.suggestThanks')}
                </div>
              ) : (
                <>
                  <input
                    value={suggestName}
                    onChange={(e) => setSuggestName(e.target.value)}
                    placeholder={t('category.suggestNamePlaceholder')}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm mb-3
                      focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <textarea
                    value={suggestNote}
                    onChange={(e) => setSuggestNote(e.target.value)}
                    placeholder={t('category.suggestNotePlaceholder')}
                    rows={2}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm mb-1 resize-none
                      focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  {selectedPath.length > 0 && (
                    <p className="text-xs text-gray-400 mb-3">
                      {t('category.suggestUnder')} {selectedPath.map((c) => c.name).join(' › ')}
                    </p>
                  )}
                </>
              )}
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setShowSuggestModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm"
                >
                  {suggestDone ? t('common.close') : t('common.cancel')}
                </button>
                {!suggestDone && (
                  <button
                    type="button"
                    disabled={suggestBusy || !suggestName.trim()}
                    onClick={async () => {
                      setSuggestBusy(true)
                      try {
                        await productApi.submitCategorySuggestion({
                          proposed_name: suggestName.trim(),
                          parent_category_id: selectedPath[selectedPath.length - 1]?.id,
                          note: suggestNote.trim() || undefined,
                        })
                        setSuggestDone(true)
                      } catch (err) {
                        logger.error('ProductForm: category suggestion failed', err)
                      } finally {
                        setSuggestBusy(false)
                      }
                    }}
                    className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-medium text-sm disabled:opacity-50"
                  >
                    {suggestBusy ? '…' : t('category.suggestSubmit')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
